import React, { useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Panel, Toggle, PanelGroup, Button, Icon } from 'rsuite';
import {
  get,
  getOr,
  set,
  has,
  times,
  omit,
  flow,
  pick,
  size,
  startCase
} from 'lodash/fp';
import { GuestType } from '../../../../interfaces/guest';
import { deleteHouseholdMember } from '../../../../redux/sagas/routines';
import {
  getIsDeletedMember,
  getIsDeletingMember
} from '../../../../redux/Guest/selectors/routes/view';
import { getActiveColumns } from '../../../../redux/selectors/household-member-column';
import { getHouseholdTotalLabel } from '../../../../redux/selectors/client';
import { getIsHouseholdDetails } from '../../../../redux/Settings/selectors/main';
import { MODULE as GUEST_MODULE } from '../../../../constants/permissions/guest';
import HouseholdMemberView from '../../../../components/guest/HouseholdMemberView';
import HouseholdMemberForm from '../../../../components/guest/HouseholdMemberForm';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import AccessControl from '../../../../components/AccessControl';
import CompactMessage from '../../../../components/CompactMessage';
import { modifyAccess } from '../../../../helpers/permission';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import { guestsViewPath } from '../../../Paths';
import history from '../../../history';

const StyledActionBar = styled.div`
  padding: 10px 0;
  min-height: 60px;
  text-align: right;
`;

const StyledPanel = styled(Panel)`
  > .rs-panel-heading {
    padding: 10px 20px;
    color: #1675e0;
    &::before {
      top: 10px;
    }
  }
`;

const MembersSection = ({ guest, ...props }) => {
  const dispatch = useDispatch();
  const { search: queryString } = useLocation();
  const members = get('othersHousehold', guest) || [];
  const memberFields = useSelector(getActiveColumns);
  const isDeletingMember = useSelector(getIsDeletingMember);
  const isDeletedMember = useSelector(getIsDeletedMember);
  const isHouseholdDetails = useSelector(getIsHouseholdDetails);
  const householdTotal = getOr(1, 'household_total', guest);
  const [isComplete, setIsComplete] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editModeMembers, setEditModeMembers] = useState({});
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [activeKey, setActiveKey] = useState(1);
  const householdTotalLabel = useSelector(getHouseholdTotalLabel);
  const isNewGuest = useMemo(
    () => {
      const query = qs.parse(queryString, { ignoreQueryPrefix: true });
      return !!get('isNew', query);
    },
    [queryString]
  );

  const memberDefaultValues = useMemo(
    () =>
      flow(
        omit(['id', 'gender', 'race']),
        pick(memberFields)
      )(guest),
    [guest, memberFields]
  );

  const membersIteretor = useMemo(
    () => {
      const membersCount = size(members);
      return membersCount >= householdTotal ? membersCount : householdTotal - 1;
    },
    [householdTotal, members]
  );

  useEffect(
    () => {
      const existingCount = size(members);
      const totalCount = householdTotal - 1;
      const isMembersCompleted = existingCount >= totalCount;
      setIsComplete(isMembersCompleted);
      setShowAll(!isMembersCompleted && !isNewGuest);
      if (!isMembersCompleted) {
        setActiveKey(existingCount + 1);
      }
    },
    [isNewGuest, householdTotal, members]
  );

  useEffect(
    () => {
      if (isDeletedMember) setShowConfirmDelete(false);
    },
    [isDeletedMember]
  );

  useEffect(
    () => {
      const id = get('id', guest);
      if (id && !isHouseholdDetails) {
        history.push(pagePathToUrl(guestsViewPath, { id, section: 'primary' }));
      }
    },
    [isHouseholdDetails]
  );

  const handleChangeView = value => {
    setShowAll(value);
  };

  const handleSelectPanel = key => {
    if (showAll) setShowAll(false);
    if (key === activeKey) setActiveKey(0);
    else setActiveKey(key);
  };

  const handleShowAddForm = () => {
    setShowAddForm(true);
    setEditModeMembers({});
    setActiveKey(householdTotal);
  };

  const handleCloseAddMemberForm = () => {
    setShowAddForm(false);
    setEditModeMembers({});
    setActiveKey(0);
  };

  const handleEditAction = index => () => {
    setEditModeMembers(set(index, true, editModeMembers));
  };

  const handleRemoveAction = index => () => {
    setMemberToDelete(members[index]);
    setShowConfirmDelete(true);
  };

  const handleCloseForm = index => () => {
    setEditModeMembers(omit(index, editModeMembers));
  };

  const handleOnSaved = index => () => {
    const updatedEditModes = omit(index, editModeMembers);
    if (!isNewGuest || isComplete) setEditModeMembers(updatedEditModes);
    else setEditModeMembers(set(index + 1, true, updatedEditModes));
  };

  const handleOnSavedAddMember = () => {
    setShowAddForm(false);
    setActiveKey(householdTotal);
  };

  const handleCloseDeleteModal = () => {
    setShowConfirmDelete(false);
  };

  const handlConfirmDelete = () => {
    const { id } = memberToDelete;
    dispatch(deleteHouseholdMember({ id, guestId: guest.id }));
  };

  return (
    <div {...props}>
      <StyledActionBar>
        <AccessControl allowOnlyFor={modifyAccess(GUEST_MODULE)}>
          <Button
            size="sm"
            appearance="primary"
            disabled={showAddForm || !isComplete}
            onClick={handleShowAddForm}
          >
            <Icon icon="plus-circle" /> Add Member
          </Button>
        </AccessControl>
        {size(members) > 0 && (
          <Toggle
            checked={showAll}
            checkedChildren="Show All"
            unCheckedChildren="Show All"
            onChange={handleChangeView}
            style={{ zIndex: 1, margin: '8px 15px' }}
          />
        )}
      </StyledActionBar>
      {!isComplete && (
        <CompactMessage
          showIcon
          type="info"
          description={
            isNewGuest
              ? 'Almost Done! Please continue filling out forms below for other member(s)'
              : 'Some details are missing. Please fill out forms below'
          }
        />
      )}
      {membersIteretor > householdTotal - 1 && (
        <CompactMessage
          showIcon
          type="info"
          description={`${householdTotalLabel} does not match the total number of Household Members. Update members below or go to Household ➔ "${householdTotalLabel}" to adjust the value.`}
        />
      )}
      <PanelGroup
        bordered
        activeKey={activeKey}
        onSelect={handleSelectPanel}
        {...(!showAll ? { accordion: true } : {})}
        style={!showAll ? { cursor: 'pointer' } : {}}
      >
        {times(i => {
          const key = i + 1;
          const member = get(i, members);
          const isEdit = has(i, editModeMembers);
          const headerLabel = member ? startCase(member.name) : `Member ${key}`;
          return (
            <StyledPanel
              eventKey={key}
              key={`member-${key}`}
              header={headerLabel}
            >
              {!member || isEdit ? (
                <HouseholdMemberForm
                  data={{ ...memberDefaultValues, ...member }}
                  guestId={get('id', guest)}
                  onSaved={handleOnSaved(i)}
                  onClose={handleCloseForm(i)}
                />
              ) : (
                <HouseholdMemberView
                  data={member}
                  onEdit={handleEditAction(i)}
                  onDelete={handleRemoveAction(i)}
                />
              )}
            </StyledPanel>
          );
        }, membersIteretor)}

        <StyledPanel
          eventKey={householdTotal}
          key={`member-${householdTotal}`}
          header={`Member ${householdTotal}`}
          style={{ display: showAddForm ? 'block' : 'none' }}
        >
          <HouseholdMemberForm
            data={memberDefaultValues}
            guestId={get('id', guest)}
            onClose={handleCloseAddMemberForm}
            onSaved={handleOnSavedAddMember}
            allowClose
          />
        </StyledPanel>
      </PanelGroup>
      <DeleteConfirmationModal
        message={`Are you sure you want to delete member "${startCase(
          get('name', memberToDelete)
        )}"?`}
        show={showConfirmDelete}
        onHide={handleCloseDeleteModal}
        onConfirm={handlConfirmDelete}
        isDeleting={isDeletingMember}
      />
    </div>
  );
};

MembersSection.propTypes = {
  guest: GuestType.isRequired
};

export default MembersSection;
